import {
  FiActivity,
  FiArchive,
  FiBookOpen,
  FiBox,
  FiBriefcase,
  FiClipboard,
  FiCreditCard,
  FiDatabase,
  FiGrid,
  FiLayers,
  FiLogOut,
  FiSettings,
  FiSliders,
  FiTag,
  FiUsers,
} from "react-icons/fi";
import { NavLink, useLocation } from "react-router";

import type { RouteIcon, RouteKey } from "@/core/constants";
import { APP_ROUTE_BY_KEY, isRouteActive } from "@/core/router";

import type { IconType } from "react-icons";

const iconMap = {
  assets: FiBox,
  audit: FiActivity,
  categories: FiTag,
  dashboard: FiGrid,
  forms: FiClipboard,
  inventory: FiDatabase,
  jobs: FiBriefcase,
  library: FiBookOpen,
  packs: FiArchive,
  plans: FiCreditCard,
  settings: FiSettings,
  team: FiUsers,
  templates: FiLayers,
  tenants: FiUsers,
  verticals: FiSliders,
} as const satisfies Record<RouteIcon, IconType>;

const sections: readonly {
  readonly items: readonly RouteKey[];
  readonly title: string;
}[] = [
  {
    title: "Core",
    items: ["superAdminDashboard", "superAdminTenants", "superAdminPlans"],
  },
  {
    title: "Library",
    items: [
      "superAdminVerticals",
      "superAdminCategories",
      "superAdminTemplates",
      "superAdminPacks",
    ],
  },
  {
    title: "Governance",
    items: ["superAdminAccessPresets", "superAdminAuditLog"],
  },
];

const baseLinkClasses =
  "mb-0.5 flex items-center justify-between gap-2 rounded-control px-3 py-2 text-[13px] transition-colors";

function SidebarLink({
  pathname,
  routeKey,
}: {
  readonly pathname: string;
  readonly routeKey: RouteKey;
}) {
  const route = APP_ROUTE_BY_KEY[routeKey];
  const active = isRouteActive(pathname, route);
  const Icon = iconMap[route.icon];

  return (
    <NavLink
      className={
        active
          ? `${baseLinkClasses} bg-sidebar-hover font-extrabold text-white`
          : `${baseLinkClasses} text-sidebar-text hover:bg-sidebar-hover hover:text-white`
      }
      to={route.path}
    >
      <span className="flex min-w-0 items-center gap-2.5">
        <Icon aria-hidden="true" className="h-3.5 w-3.5 shrink-0 opacity-80" />
        <span className="truncate">{route.label}</span>
      </span>
    </NavLink>
  );
}

function SidebarLogoutButton({
  onSignOut,
}: {
  readonly onSignOut: () => void;
}) {
  return (
    <button
      className={`${baseLinkClasses} w-full border-0 bg-transparent text-left text-sidebar-text hover:bg-sidebar-hover hover:text-white`}
      onClick={onSignOut}
      type="button"
    >
      <span className="flex items-center gap-2.5">
        <FiLogOut
          aria-hidden="true"
          className="h-3.5 w-3.5 shrink-0 opacity-80"
        />
        <span>Log out</span>
      </span>
    </button>
  );
}

export function Sidebar({ onSignOut }: { readonly onSignOut: () => void }) {
  const location = useLocation();

  return (
    <aside className="flex flex-col overflow-y-auto bg-sidebar px-3 py-[18px] text-white">
      <div className="mb-3 flex items-center gap-2.5 border-b border-sidebar-hover px-2.5 pb-[18px]">
        <div className="flex h-8 w-8 items-center justify-center rounded-control bg-cta text-sm font-extrabold">
          S
        </div>
        <div>
          <div className="text-sm font-extrabold leading-5">SiteHub</div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-sidebar-subtle">
            Super Admin
          </div>
        </div>
      </div>
      <nav aria-label="Sidebar navigation" className="min-h-0 flex-1">
        {sections.map((section) => (
          <div key={section.title}>
            <div className="px-2.5 pb-2 pt-3 text-[11px] font-extrabold uppercase tracking-[0.08em] text-sidebar-muted">
              {section.title}
            </div>
            {section.items.map((routeKey) => (
              <SidebarLink
                key={routeKey}
                pathname={location.pathname}
                routeKey={routeKey}
              />
            ))}
          </div>
        ))}
      </nav>
      <div className="mt-auto border-t border-sidebar-hover pt-3">
        <SidebarLogoutButton onSignOut={onSignOut} />
      </div>
    </aside>
  );
}
